import type { AxiosRequestConfig } from "axios";
import axios from "axios";
import qs from "qs";

import { config } from "~/data";
import { fetchAiEngineData } from "~/lib/ai-engine/apis";
import { AiEngineDataType, OnBoardingType } from "~/lib/ai-engine/enums";
import type { AiOnBoardResponse } from "~/lib/ai-engine/types";

import type {
  FetchUserTypeResponse,
  OnboardingSlide,
  OnboardQueryStringPayload,
  RecommendedEntityIds,
  RecommendedOnboardingFunction,
  UserType,
} from "./types";

type DetermineUserTypeParameters = {
  forcedQaUserType: string;
  gaClientId: string;
  isAmp: boolean;
  isFinishedMyNews: boolean;
  isScmpSubscriber: boolean;
};

export const axiosOptions: AxiosRequestConfig = {
  withCredentials: true,
};

export const determineUserType = async ({
  forcedQaUserType,
  gaClientId,
  isAmp,
  isFinishedMyNews,
  isScmpSubscriber,
}: DetermineUserTypeParameters) => {
  const parsedQueryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as unknown as OnboardQueryStringPayload;

  if (config.general.env !== "production" && forcedQaUserType) return forcedQaUserType as UserType;
  if (isScmpSubscriber) return "subscribers";
  if (isFinishedMyNews) return "myNews";
  if (isAmp) return "amp";
  if (
    parsedQueryString.firstTimeRegister === "true" &&
    parsedQueryString.signUpComplete === "true"
  ) {
    return "newsletter";
  }
  return await fetchUserType(gaClientId);
};

const fetchUserType = async (gaClientId: string) => {
  const url = new URL(`${config.general.userTypeHost}/client-id/${gaClientId}`);

  try {
    const response = await axios.get<FetchUserTypeResponse>(url.toString(), axiosOptions);

    return response?.data.data?.user_types
      ? getUserTypeFromList((response?.data.data?.user_types ?? []).map(element => element.id))
      : "default";
  } catch {
    return "default";
  }
};

export const generateRecommendedPromises = (
  onboardingSlides: OnboardingSlide[],
  scmpUserUuid: string,
) => {
  const fetchProvider: Partial<Record<OnboardingSlide, RecommendedOnboardingFunction>> = {
    conversations: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.Conversations]
        >({
          sc: OnBoardingType.Conversations,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(article => article.id) ?? [],
          },
          key: "conversations",
        };
      },
    },
    evergreenArticles: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.EvergreenArticles]
        >({
          sc: OnBoardingType.EvergreenArticles,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(article => article.id) ?? [],
          },
          key: "evergreenArticles",
        };
      },
    },
    myNews: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.MyNews]
        >({
          sc: OnBoardingType.MyNews,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            topicEntityIds: recommend.data?.topic?.map?.(topic => topic.id) ?? [],
          },
          key: "myNews",
        };
      },
    },
    recommendedArticles: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.RecommendedArticles]
        >({
          sc: OnBoardingType.RecommendedArticles,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(topic => topic.id) ?? [],
          },
          key: "recommendedArticles",
        };
      },
    },
  };

  const fetchers = onboardingSlides.reduce<(() => RecommendedOnboardingFunction["fetcher"])[]>(
    (array, key) => {
      const data = fetchProvider[key];
      data && array.push(() => data.fetcher);
      return array;
    },
    [],
  );

  return fetchers;
};

export const sanitizePromisesToMap = (resolvedPromises: RecommendedEntityIds[]) => {
  if (resolvedPromises.length === 0) return null;
  return resolvedPromises.reduce(
    (accumulator, element) => ({
      ...accumulator,
      [element.key]: {
        articleEntityIds: element.data.articleEntityIds ?? [],
        authorEntityIds: element.data.authorEntityIds ?? [],
        topicEntityIds: element.data.topicEntityIds ?? [],
      },
    }),
    {} as Record<OnboardingSlide, Required<RecommendedEntityIds["data"]>>,
  );
};

// NOTE: the order in which these user type appears is important and has been sorted by order of
//       importance
const getUserTypeFromList = (userTypeList: string[]): UserType => {
  if (userTypeList.includes("028")) return "028";
  if (userTypeList.includes("029")) return "029";
  if (userTypeList.includes("038")) return "038";
  if (userTypeList.includes("083")) return "083";
  if (userTypeList.includes("084")) return "084";
  if (userTypeList.includes("058")) return "058";
  if (userTypeList.includes("008")) return "008";
  if (userTypeList.includes("057")) return "057";
  if (userTypeList.includes("016")) return "016";
  if (userTypeList.includes("017")) return "017";
  if (userTypeList.includes("030")) return "030";
  if (userTypeList.includes("014")) return "014";

  return "default";
};
